import React from 'react';
import { useLanguage } from '../context/LanguageContext';

const EN = {
  title: 'Important Information for U.S. Customers',
  intro: [
    'Effective August 22, 2026, new U.S. import tariffs affecting certain Canadian-made products came into effect.',
    'MyGuardPod has adjusted its U.S. Etsy pricing to account for the additional import tariff currently affecting our U.S. shipments.',
  ],
  blocks: [
    {
      heading: 'What does this mean for you?',
      highlight: 'The price you see on our Etsy listing already accounts for the current U.S. import tariff.',
      paragraphs: [
        'You will not be asked to pay an additional 50% tariff when your MyGuardPod order is delivered.',
        'We have chosen to manage these import costs as part of our U.S. pricing so that our customers know what they are paying when they place their order and are not surprised by an unexpected tariff charge at delivery.',
      ],
    },
    {
      heading: 'Why are U.S. prices different?',
      paragraphs: [
        'The additional cost reflected in U.S. pricing is caused by the current U.S. import tariff on affected Canadian-made goods.',
        'This is not a general MyGuardPod price increase.',
        'MyGuardPod products continue to be designed and manufactured in Canada.',
      ],
    },
    {
      heading: 'What happens if the tariff changes?',
      paragraphs: [
        'We are monitoring the situation.',
        'If the applicable U.S. tariffs are reduced, suspended, removed, or otherwise changed, we will adjust our U.S. pricing accordingly.',
        'Our goal is to keep pricing as fair and transparent as possible while continuing to make MyGuardPod products available to our U.S. customers.',
      ],
    },
  ],
  closing: 'Thank you for supporting a small Canadian manufacturer.',
  signature: 'MyGuardPod',
  tagline: 'Protect what protects you.',
};

const FR = {
  title: 'Information importante pour les clients am\u00e9ricains',
  intro: [
    'Depuis le 22 ao\u00fbt 2026, de nouveaux tarifs d\u2019importation am\u00e9ricains visant certains produits fabriqu\u00e9s au Canada sont en vigueur.',
    'MyGuardPod a ajust\u00e9 ses prix Etsy am\u00e9ricains pour tenir compte du tarif d\u2019importation additionnel qui touche actuellement nos envois vers les \u00c9tats-Unis.',
  ],
  blocks: [
    {
      heading: 'Qu\u2019est-ce que cela signifie pour vous?',
      highlight: 'Le prix affich\u00e9 sur notre fiche Etsy tient d\u00e9j\u00e0 compte du tarif d\u2019importation am\u00e9ricain actuel.',
      paragraphs: [
        'Aucun tarif additionnel de 50 % ne vous sera demand\u00e9 \u00e0 la livraison de votre commande MyGuardPod.',
        'Nous avons choisi de g\u00e9rer ces co\u00fbts d\u2019importation dans nos prix am\u00e9ricains afin que nos clients sachent exactement ce qu\u2019ils paient au moment de commander, sans surprise de frais douaniers \u00e0 la livraison.',
      ],
    },
    {
      heading: 'Pourquoi les prix am\u00e9ricains sont-ils diff\u00e9rents?',
      paragraphs: [
        'Le co\u00fbt additionnel refl\u00e9t\u00e9 dans les prix am\u00e9ricains d\u00e9coule du tarif d\u2019importation am\u00e9ricain actuel sur les produits canadiens vis\u00e9s.',
        'Il ne s\u2019agit pas d\u2019une hausse g\u00e9n\u00e9rale des prix MyGuardPod.',
        'Les produits MyGuardPod continuent d\u2019\u00eatre con\u00e7us et fabriqu\u00e9s au Canada.',
      ],
    },
    {
      heading: 'Que se passe-t-il si le tarif change?',
      paragraphs: [
        'Nous suivons la situation de pr\u00e8s.',
        'Si les tarifs am\u00e9ricains applicables sont r\u00e9duits, suspendus, retir\u00e9s ou autrement modifi\u00e9s, nous ajusterons nos prix am\u00e9ricains en cons\u00e9quence.',
        'Notre objectif est de garder des prix aussi justes et transparents que possible tout en continuant d\u2019offrir les produits MyGuardPod \u00e0 nos clients am\u00e9ricains.',
      ],
    },
  ],
  closing: 'Merci de soutenir un petit fabricant canadien.',
  signature: 'MyGuardPod',
  tagline: 'Prot\u00e9gez ce qui vous prot\u00e8ge.',
};

const USCustomersInfo = () => {
  const { language } = useLanguage();
  const c = language === 'fr' ? FR : EN;
  return (
    <section
      id="us-customers"
      data-testid="us-customers-info"
      className="scroll-mt-28 bg-neutral-50 rounded-3xl border border-neutral-200 p-6 sm:p-10"
    >
      <p className="font-mono text-xs uppercase tracking-widest text-neutral-500 mb-3">
        <span aria-hidden="true" className="mr-2">🇺🇸</span>
        {language === 'fr' ? 'Clients am\u00e9ricains' : 'U.S. Customers'}
      </p>
      <h2 className="font-outfit font-black text-2xl sm:text-3xl tracking-tight text-[#1a1a1a] mb-6">
        {c.title}
      </h2>
      <div className="space-y-3 mb-8">
        {c.intro.map((p, i) => (
          <p key={i} className="font-manrope text-neutral-600 leading-relaxed">{p}</p>
        ))}
      </div>
      <div className="space-y-8">
        {c.blocks.map((b, i) => (
          <div key={i}>
            <h3 className="font-outfit font-bold text-sm uppercase tracking-widest text-[#E31B23] mb-3">
              {b.heading}
            </h3>
            {b.highlight && (
              <p
                data-testid="us-customers-highlight"
                className="font-outfit font-bold text-lg sm:text-xl text-[#1a1a1a] leading-snug mb-3"
              >
                {b.highlight}
              </p>
            )}
            <div className="space-y-2">
              {b.paragraphs.map((p, j) => (
                <p key={j} className="font-manrope text-neutral-600 leading-relaxed">{p}</p>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-10 pt-6 border-t border-neutral-200">
        <p className="font-manrope text-neutral-600">{c.closing}</p>
        <p className="font-outfit font-bold text-[#1a1a1a] mt-3">{c.signature}</p>
        <p className="font-manrope text-sm text-neutral-500 italic">{c.tagline}</p>
      </div>
    </section>
  );
};

export default USCustomersInfo;
